import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowRight, Clock, MapPin, Users, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getClassBySlug } from "@/data/classes";

function formatDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function Register() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    organization: "",
    notes: "",
    agree: false,
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/sessions/${sessionId}`);
        setSession(data);
      } catch (e) {
        toast.error("Session not found");
        navigate("/schedule");
      } finally {
        setLoading(false);
      }
    })();
  }, [sessionId, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agree) {
      toast.error("Please review and agree to the cancellation policy.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/registrations", {
        session_id: sessionId,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        organization: form.organization,
        notes: form.notes,
      });
      navigate(`/registration-success/${data.id}`, { state: data });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-20 text-center text-gray-500">Loading…</div>;
  if (!session) return null;

  const c = getClassBySlug(session.class_slug);

  return (
    <div className="bg-[#f9fafb] min-h-screen">
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
          <Link
            to="/schedule"
            className="inline-flex items-center gap-2 text-sm font-bold text-gray-600 hover:text-[#d90429]"
            data-testid="register-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to schedule
          </Link>
          <div className="mt-6 font-mono-label text-[#d90429]">Registration</div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-black text-[#0a0a0a] leading-tight">
            {c?.name || "Class Registration"}
          </h1>
          <div className="mt-6 grid sm:grid-cols-3 gap-4 text-sm">
            <div className="flex gap-2 items-start">
              <Clock className="w-5 h-5 text-[#d90429] shrink-0" />
              <div>
                <div className="font-bold text-[#0a0a0a]">{formatDate(session.date)}</div>
                <div className="text-gray-600">{session.start_time} – {session.end_time}</div>
              </div>
            </div>
            <div className="flex gap-2 items-start">
              <MapPin className="w-5 h-5 text-[#d90429] shrink-0" />
              <div className="text-gray-800">{session.location}</div>
            </div>
            <div className="flex gap-2 items-start">
              <Users className="w-5 h-5 text-[#d90429] shrink-0" />
              <div className="text-gray-800">
                {session.seats_available} seat{session.seats_available === 1 ? "" : "s"} left
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
        <form onSubmit={submit} className="bg-white border border-gray-200 rounded-lg p-8 space-y-6" data-testid="register-form">
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="First name" name="first_name" value={form.first_name} onChange={handleChange} required />
            <Field label="Last name" name="last_name" value={form.last_name} onChange={handleChange} required />
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Email" name="email" type="email" value={form.email} onChange={handleChange} required />
            <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={handleChange} required />
          </div>
          <Field label="Organization (optional)" name="organization" value={form.organization} onChange={handleChange} />
          <div>
            <label className="font-mono-label block mb-2" htmlFor="notes">Notes (optional)</label>
            <textarea
              id="notes"
              name="notes"
              rows="3"
              value={form.notes}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:border-[#d90429] focus:ring-2 focus:ring-[#d90429]/20"
              data-testid="register-input-notes"
            />
          </div>

          <div className="p-5 bg-[#f9fafb] border border-gray-200 rounded-md text-sm text-gray-700">
            <div className="font-bold text-[#0a0a0a] mb-2">Cancellation Policy</div>
            Full refunds available up to 48 hours before class start. Later
            cancellations receive credit toward a future class. No-shows are
            non-refundable. Payment is collected on-site (no online payments yet).
          </div>

          <label className="flex items-start gap-3 text-sm text-gray-800">
            <input
              type="checkbox"
              name="agree"
              checked={form.agree}
              onChange={handleChange}
              className="mt-1 w-4 h-4"
              data-testid="register-agree-checkbox"
            />
            <span>I have read and agree to the cancellation policy.</span>
          </label>

          <button
            type="submit"
            disabled={submitting}
            data-testid="register-submit-btn"
            className="w-full inline-flex justify-center items-center gap-2 rounded-md bg-[#d90429] px-8 py-4 text-base font-bold text-white hover:bg-black transition-colors disabled:opacity-60"
          >
            {submitting ? "Submitting…" : "Complete Registration"} <ArrowRight className="w-5 h-5" />
          </button>
        </form>
      </section>
    </div>
  );
}

function Field({ label, name, type = "text", value, onChange, required }) {
  return (
    <div>
      <label className="font-mono-label block mb-2" htmlFor={name}>
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={`register-input-${name.replace("_", "-")}`}
        className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:border-[#d90429] focus:ring-2 focus:ring-[#d90429]/20"
      />
    </div>
  );
}
